interface Product {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  cover_image: string;
  media_files: Array<{ url: string; type: 'image' | 'video'; name: string }> | null;
  file_url: string;
  product_type: string;
  stock_unlimited: boolean;
  stock_quantity: number;
  shipping_info: string;
  weight: number;
}

interface StoreProfile {
  store_name: string;
  store_description: string;
  banner_image: string;
  logo_image: string;
  social_links?: Array<{ id: string; platform: string; label: string; url: string; is_active: boolean }>;
}

interface Profile {
  full_name: string;
  avatar_url: string;
}

interface Props {
  storeProfile: StoreProfile | null;
  userProfile: Profile | null;
  products: Product[];
  storeName: string;
  storeDesc: string;
  onBuyNow: (product: Product) => void;
  onCardClick: (product: Product) => void;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  activeFilter: 'all' | 'digital' | 'physical';
  setActiveFilter: (v: 'all' | 'digital' | 'physical') => void;
  filteredProducts: Product[];
}

const LINK_PLATFORMS: Record<string, { icon: string; color: string }> = {
  website:   { icon: 'ri-global-line',           color: '#a08070' },
  instagram: { icon: 'ri-instagram-line',         color: '#c4a882' },
  twitter:   { icon: 'ri-twitter-x-line',         color: '#3d2b1f' },
  facebook:  { icon: 'ri-facebook-circle-line',   color: '#7c5c47' },
  tiktok:    { icon: 'ri-tiktok-line',            color: '#3d2b1f' },
  youtube:   { icon: 'ri-youtube-line',           color: '#c4a882' },
  linkedin:  { icon: 'ri-linkedin-box-line',      color: '#7c5c47' },
  whatsapp:  { icon: 'ri-whatsapp-line',          color: '#a08070' },
  telegram:  { icon: 'ri-telegram-line',          color: '#a08070' },
  custom:    { icon: 'ri-links-line',             color: '#c4a882' },
};

function isVideoUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase().split('?')[0];
  return lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.ogg');
}

const categoryIcons: Record<string, string> = {
  'e-book': 'ri-book-line', 'template': 'ri-file-text-line', 'course': 'ri-graduation-cap-line',
  'audio': 'ri-music-line', 'video': 'ri-video-line', 'software': 'ri-code-box-line',
  'clothing': 'ri-t-shirt-line', 'accessories': 'ri-handbag-line', 'art': 'ri-paint-brush-line',
  'electronics': 'ri-cpu-line', 'books': 'ri-book-open-line', 'other': 'ri-folder-line',
};

export default function BoutiqueTemplate({ storeProfile, userProfile, products, storeName, storeDesc, onBuyNow, onCardClick, searchQuery, setSearchQuery, activeFilter, setActiveFilter, filteredProducts }: Props) {
  const digitalCount = products.filter(p => p.product_type !== 'physical').length;
  const physicalCount = products.filter(p => p.product_type === 'physical').length;
  const activeLinks = (storeProfile?.social_links || []).filter(l => l.is_active);

  return (
    <div className="min-h-screen font-sans" style={{ background: '#faf7f2' }}>
      {/* Hero Banner */}
      <div className="relative w-full h-80 overflow-hidden">
        {storeProfile?.banner_image ? (
          <img src={storeProfile.banner_image} alt="banner" className="w-full h-full object-cover object-top" />
        ) : (
          <img
            src="https://public.readdy.ai/ai/img_res/dbc17a21f379015fa1912d0754e41ad2.jpg"
            alt="banner"
            className="w-full h-full object-cover object-top"
          />
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(250,247,242,0.95))' }}></div>
        {/* Centered store name over banner */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          {(storeProfile?.logo_image || userProfile?.avatar_url) && (
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg mb-3">
              <img src={storeProfile?.logo_image || userProfile?.avatar_url || ''} alt="logo" className="w-full h-full object-cover" />
            </div>
          )}
          <h1 className="text-4xl font-black tracking-widest uppercase mb-2" style={{ color: '#3d2b1f', fontFamily: 'Georgia, serif', letterSpacing: '0.15em' }}>{storeName}</h1>
          <p className="text-sm max-w-md leading-relaxed" style={{ color: '#7c5c47' }}>{storeDesc}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Stats row */}
        <div className="flex items-center justify-center gap-8 mb-10 pb-8" style={{ borderBottom: '1px solid #e8ddd4' }}>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: '#3d2b1f' }}>{products.length}</div>
            <div className="text-xs uppercase tracking-widest mt-0.5" style={{ color: '#a08070' }}>Products</div>
          </div>
          {digitalCount > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#3d2b1f' }}>{digitalCount}</div>
              <div className="text-xs uppercase tracking-widest mt-0.5" style={{ color: '#a08070' }}>Digital</div>
            </div>
          )}
          {physicalCount > 0 && (
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#3d2b1f' }}>{physicalCount}</div>
              <div className="text-xs uppercase tracking-widest mt-0.5" style={{ color: '#a08070' }}>Physical</div>
            </div>
          )}
        </div>

        {/* Linktree Section */}
        {activeLinks.length > 0 && (
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-center mb-4" style={{ color: '#a08070' }}>Links</p>
            <div className="flex flex-wrap justify-center gap-3">
              {activeLinks.map(link => {
                const meta = LINK_PLATFORMS[link.platform] || LINK_PLATFORMS.custom;
                return (
                  <a
                    key={link.id}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full transition-all group cursor-pointer"
                    style={{ background: '#fff', border: '1px solid #e8ddd4' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#c4a882'; e.currentTarget.style.background = '#f5ede4'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8ddd4'; e.currentTarget.style.background = '#fff'; }}
                  >
                    <i className={`${meta.icon} text-sm`} style={{ color: meta.color }}></i>
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#7c5c47' }}>{link.label}</span>
                    <i className="ri-external-link-line text-xs" style={{ color: '#d4b896' }}></i>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-10">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: '#c4a882' }}></i>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search collection..."
              className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm outline-none"
              style={{ background: '#fff', border: '1px solid #e8ddd4', color: '#3d2b1f' }}
            />
          </div>
          <div className="flex items-center gap-2">
            {(['all', 'digital', 'physical'] as const).map(f => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className="px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-widest transition-all cursor-pointer whitespace-nowrap"
                style={activeFilter === f
                  ? { background: '#3d2b1f', color: '#faf7f2' }
                  : { background: '#fff', color: '#a08070', border: '1px solid #e8ddd4' }}
              >
                {f === 'all' ? 'All' : f === 'digital' ? 'Digital' : 'Physical'}
              </button>
            ))}
          </div>
        </div>

        {/* Products */}
        {filteredProducts.length === 0 ? (
          <div className="py-24 text-center">
            <i className="ri-store-2-line text-5xl mb-4 block" style={{ color: '#e8ddd4' }}></i>
            <p className="text-lg font-semibold" style={{ color: '#a08070' }}>{searchQuery ? 'Nothing found' : 'Collection coming soon'}</p>
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="mt-4 px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-widest cursor-pointer whitespace-nowrap" style={{ background: '#3d2b1f', color: '#faf7f2' }}>
                Clear
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-16">
            {filteredProducts.map(product => {
              const isPhysical = product.product_type === 'physical';
              const outOfStock = !product.stock_unlimited && product.stock_quantity <= 0;
              const firstMedia = product.media_files && product.media_files.length > 0 ? product.media_files[0] : null;
              const src = firstMedia ? firstMedia.url : product.cover_image;
              const isVid = firstMedia ? firstMedia.type === 'video' : isVideoUrl(product.cover_image);
              return (
                <div
                  key={product.id}
                  onClick={() => onCardClick(product)}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-xl mb-3 bg-white shadow-sm" style={{ minHeight: '200px' }}>
                    {src ? (
                      isVid ? (
                        <video src={src} className="w-full h-auto object-contain" muted autoPlay loop playsInline />
                      ) : (
                        <img src={src} alt={product.title} className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-500" />
                      )
                    ) : (
                      <div className="w-full h-48 flex flex-col items-center justify-center" style={{ background: '#f5ede4' }}>
                        <i className={`${categoryIcons[product.category] || 'ri-file-line'} text-5xl mb-2`} style={{ color: '#d4b896' }}></i>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#faf7f2', color: '#a08070' }}>
                        {isPhysical ? 'Physical' : 'Digital'}
                      </span>
                    </div>
                    {outOfStock && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(250,247,242,0.85)' }}>
                        <span className="text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full" style={{ border: '1px solid #c4a882', color: '#a08070' }}>Sold Out</span>
                      </div>
                    )}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4" style={{ background: 'linear-gradient(to top, rgba(61,43,31,0.5), transparent)' }}>
                      <button
                        onClick={e => { e.stopPropagation(); if (!outOfStock) onBuyNow(product); }}
                        disabled={outOfStock}
                        className="px-6 py-2 rounded-full text-xs font-semibold uppercase tracking-widest cursor-pointer whitespace-nowrap"
                        style={{ background: '#faf7f2', color: '#3d2b1f' }}
                      >
                        {outOfStock ? 'Sold Out' : isPhysical ? 'Buy Now' : 'Get Now'}
                      </button>
                    </div>
                  </div>
                  <div className="text-center px-1">
                    <h3 className="text-sm font-semibold line-clamp-1 mb-1" style={{ color: '#3d2b1f' }}>{product.title}</h3>
                    <p className="text-base font-bold" style={{ color: '#7c5c47' }}>{product.currency} {product.price.toLocaleString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="py-8" style={{ background: '#3d2b1f' }}>
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs uppercase tracking-widest" style={{ color: '#c4a882' }}>Powered by EventHub Store</span>
          <div className="flex items-center gap-4 text-xs" style={{ color: '#c4a882' }}>
            <span className="flex items-center gap-1"><i className="ri-shield-check-line"></i> Secure</span>
            <span className="flex items-center gap-1"><i className="ri-lock-line"></i> Protected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
